import { useAuth } from "@/hooks/use-auth";
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Shield, Lock, Loader2, Plug, Trash2, Copy } from "lucide-react";
import { queryClient } from "@/lib/queryClient";

const passwordSchema = z.object({
  currentPassword: z.string().min(1, "Current password is required"),
  newPassword: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string().min(1, "Please confirm your password"),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type PasswordFormValues = z.infer<typeof passwordSchema>;

export default function SecurityPage() {
  const { user } = useAuth();
  const { toast } = useToast();

  const form = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const passwordMutation = useMutation({
    mutationFn: async (values: PasswordFormValues) => {
      const res = await apiRequest("PATCH", "/api/profile/password", {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      return res.json();
    },
    onSuccess: () => {
      form.reset();
      toast({ title: "Password updated", description: "Your password has been changed successfully." });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });
  const { data: mcpData, isLoading: mcpLoading } = useQuery<{ connections: Array<{ id: string; label: string; expiresAt: string; lastUsedAt: string | null; revokedAt: string | null }> }>({ queryKey: ["/api/mcp/connections"] });
  const [newToken, setNewToken] = useState<string | null>(null);
  const createMcp = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/mcp/connections", { label: "ChatGPT" })).json() as Promise<{ accessToken: string }>,
    onSuccess: (data) => { setNewToken(data.accessToken); queryClient.invalidateQueries({ queryKey: ["/api/mcp/connections"] }); toast({ title: "MCP connection created", description: "Copy the token now; it will not be shown again." }); },
    onError: (err: Error) => toast({ title: "Could not create connection", description: err.message, variant: "destructive" }),
  });
  const revokeMcp = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/mcp/connections/${id}`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["/api/mcp/connections"] }); toast({ title: "MCP connection revoked" }); },
    onError: (err: Error) => toast({ title: "Could not revoke connection", description: err.message, variant: "destructive" }),
  });

  return (
    <div className="max-w-2xl mx-auto py-10 px-4 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" data-testid="text-security-title">Security</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage your account security settings</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Lock className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Change Password</CardTitle>
              <CardDescription>Update your password to keep your account secure</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit((v) => passwordMutation.mutate(v))} className="space-y-4">
              <FormField
                control={form.control}
                name="currentPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Current Password</FormLabel>
                    <FormControl>
                      <Input {...field} type="password" data-testid="input-current-password" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New Password</FormLabel>
                    <FormControl>
                      <Input {...field} type="password" data-testid="input-new-password" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm New Password</FormLabel>
                    <FormControl>
                      <Input {...field} type="password" data-testid="input-confirm-password" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex justify-end pt-2">
                <Button type="submit" disabled={passwordMutation.isPending} data-testid="button-change-password">
                  {passwordMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Shield className="h-4 w-4 mr-2" />
                  )}
                  Update Password
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10"><Plug className="h-5 w-5 text-primary" /></div>
            <div><CardTitle className="text-lg">AI assistant connections</CardTitle><CardDescription>Connect your Persona OS account to Claude, ChatGPT, or another OAuth-compatible MCP client. Connections can only read brands you can access.</CardDescription></div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">For Claude or another OAuth-compatible client, add the remote MCP endpoint <code className="rounded bg-muted px-1">/mcp</code>, then sign in here and approve the connection. If your client does not support OAuth, create a manual Bearer token below; tokens expire after 90 days and are displayed only once.</p>
          {newToken && <div className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3"><p className="text-sm font-medium">Copy this token now</p><code className="block break-all text-xs">{newToken}</code><Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(newToken)}><Copy className="mr-2 h-3.5 w-3.5" />Copy token</Button></div>}
          <Button onClick={() => createMcp.mutate()} disabled={createMcp.isPending} data-testid="button-create-mcp-connection">{createMcp.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Create manual token</Button>
          <div className="space-y-2">
            {mcpLoading ? <p className="text-sm text-muted-foreground">Loading connections…</p> : mcpData?.connections.filter((c) => !c.revokedAt).map((c) => <div key={c.id} className="flex items-center justify-between rounded border p-3 text-sm"><div><p className="font-medium">{c.label}</p><p className="text-muted-foreground">Expires {new Date(c.expiresAt).toLocaleDateString()}{c.lastUsedAt ? ` · Last used ${new Date(c.lastUsedAt).toLocaleDateString()}` : ""}</p></div><Button size="sm" variant="outline" onClick={() => revokeMcp.mutate(c.id)} disabled={revokeMcp.isPending} data-testid={`button-revoke-mcp-${c.id}`}><Trash2 className="mr-1 h-3.5 w-3.5" />Revoke</Button></div>)}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Account Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between items-center py-2 border-b border-border/50">
            <span className="text-sm text-muted-foreground">Email</span>
            <span className="text-sm font-medium" data-testid="text-security-email">{user?.email}</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-border/50">
            <span className="text-sm text-muted-foreground">Account Type</span>
            <span className="text-sm font-medium" data-testid="text-account-type">{user?.isMaster ? "Master Admin" : (user as any)?.isSuperuser ? "Superuser" : "Standard User"}</span>
          </div>
          <div className="flex justify-between items-center py-2">
            <span className="text-sm text-muted-foreground">Member Since</span>
            <span className="text-sm font-medium" data-testid="text-member-since">
              {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : "—"}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
